import { AbcAnalysisProductDetailRow } from '../types/abc-analysis-product-detail-row';

// Marks the synthetic aggregate row prepended by buildProductDetailWithTotal below.
export const ABC_ANALYSIS_PRODUCT_DETAIL_TOTAL_SALES_ORG = '__TOTAL__';

// Fields that aren't meaningful summed across sales organisations (dates, unit prices,
// listing flag) are left null on the total row rather than faked with a "first row wins"
// value — soft-ds-cell already renders null as its configured empty text.
export type AbcAnalysisProductDetailWithTotalRow = Omit<
  AbcAnalysisProductDetailRow,
  'listed' | 'last_sales_date' | 'first_purchase_date' | 'last_purchase_price_date' | 'last_purchase_price' | 'sales_price' | 'days_not_sold'
> & {
  isTotal: boolean;
  listed: string | null;
  last_sales_date: string | null;
  first_purchase_date: string | null;
  last_purchase_price_date: string | null;
  last_purchase_price: number | null;
  sales_price: number | null;
  days_not_sold: number | null;
};

function percentOf(part: number, total: number): number {
  return total === 0 ? 0 : (part / total) * 100;
}

export function buildProductDetailWithTotal(
  rows: readonly AbcAnalysisProductDetailRow[]
): AbcAnalysisProductDetailWithTotalRow[] {
  const detailRows: AbcAnalysisProductDetailWithTotalRow[] = rows.map((row) => ({
    ...row,
    isTotal: false,
  }));

  if (rows.length === 0) return detailRows;

  const grandTotal = rows.reduce(
    (acc, row) => ({
      net: acc.net + row.net,
      contributionMargin: acc.contributionMargin + row.contribution_margin,
      salesQuantity: acc.salesQuantity + row.sales_quantity,
      inventoryValue: acc.inventoryValue + row.inventory_value,
      inventoryQuantity: acc.inventoryQuantity + row.inventory_quantity,
      purchaseQuantity: acc.purchaseQuantity + row.purchase_quantity,
    }),
    { net: 0, contributionMargin: 0, salesQuantity: 0, inventoryValue: 0, inventoryQuantity: 0, purchaseQuantity: 0 }
  );

  const totalRow: AbcAnalysisProductDetailWithTotalRow = {
    isTotal: true,
    sales_organisation: ABC_ANALYSIS_PRODUCT_DETAIL_TOTAL_SALES_ORG,
    product_number: rows[0].product_number,
    product_description: rows[0].product_description,
    listed: null,
    net: grandTotal.net,
    contribution_margin: grandTotal.contributionMargin,
    contribution_margin_percent: percentOf(grandTotal.contributionMargin, grandTotal.net),
    sales_quantity: grandTotal.salesQuantity,
    product_unit: rows[0].product_unit,
    inventory_value: grandTotal.inventoryValue,
    inventory_quantity: grandTotal.inventoryQuantity,
    unit_cost: grandTotal.inventoryQuantity === 0 ? null : grandTotal.inventoryValue / grandTotal.inventoryQuantity,
    purchase_quantity: grandTotal.purchaseQuantity,
    purchase_unit: rows[0].purchase_unit,
    last_purchase_price: null,
    first_purchase_date: null,
    last_purchase_price_date: null,
    sales_price: null,
    last_sales_date: null,
    days_not_sold: null,
  };

  return [totalRow, ...detailRows];
}
